import { useCallback, useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';

import { QueryEntity } from '../types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface UserMetadata {
  [userId: string]: {
    lastActiveAt: number;
    emailConfirmed: boolean;
    [key: string]: unknown;
  } | null;
}

// Cache that persists across component mounts/unmounts
const metadataCache = new Map<string, UserMetadata[string]>();
const pendingFetches = new Set<string>();
let cacheVersion = 0;

/**
 * Hook for lazy-loading user metadata with in-memory caching.
 * Only fetches metadata for users that haven't been fetched yet.
 * Cache persists across navigations and is cleared when QueryEntity.Users is invalidated.
 */
export const useLazyUserMetadata = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();
  const [currentCacheVersion, setCurrentCacheVersion] = useState(cacheVersion);

  // Listen for Users query invalidation to clear the metadata cache
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type === 'observerResultsUpdated') {
        const query = event.query;
        // Check if this is a Users query being invalidated
        if (query.queryKey[0] === QueryEntity.Users && query.state.isInvalidated) {
          // Clear the metadata cache
          metadataCache.clear();
          pendingFetches.clear();
          cacheVersion += 1;
          setCurrentCacheVersion(cacheVersion);
        }
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  const fetchMetadata = useCallback(async (userIds: string[]): Promise<void> => {
    // Filter out users that are already cached or being fetched
    const unfetchedUserIds = userIds.filter((id) => !metadataCache.has(id) && !pendingFetches.has(id));

    if (unfetchedUserIds.length === 0) {
      return;
    }

    // Mark these users as being fetched
    unfetchedUserIds.forEach((id) => pendingFetches.add(id));

    try {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          userIds: unfetchedUserIds,
        },
      });

      const response = await fetch(`${APIBaseChronos}/api/org/users/metadata`, fetchConfig);

      if (!response.ok) {
        throw new Error('Failed to fetch user metadata');
      }

      const metadata: UserMetadata = await response.json();

      // Cache the fetched metadata
      Object.entries(metadata).forEach(([userId, data]) => {
        metadataCache.set(userId, data);
      });

      // Mark users that we tried to fetch but didn't get anything back as null
      unfetchedUserIds.forEach((userId) => {
        if (!metadataCache.has(userId)) {
          metadataCache.set(userId, null);
        }
      });
    } catch (error) {
      console.error('Error fetching user metadata:', error);
    } finally {
      // Remove from pending fetches
      unfetchedUserIds.forEach((id) => pendingFetches.delete(id));
    }
  }, []);

  const getMetadata = useCallback((userId: string) => {
    return metadataCache.get(userId);
  }, []);

  const hasMetadata = useCallback((userId: string) => {
    return metadataCache.has(userId);
  }, []);

  const isPending = useCallback((userId: string) => {
    return pendingFetches.has(userId);
  }, []);

  return {
    fetchMetadata,
    getMetadata,
    hasMetadata,
    isPending,
    cacheVersion: currentCacheVersion,
  };
};
